import {AfterViewInit, Component, inject, input, OnDestroy, ViewChild} from '@angular/core';
import {RizeSessionType}                                               from '@djsharkk/logr-types';
import {ChartConfiguration, TooltipItem}                               from 'chart.js';
import {DateTime}                                                      from 'luxon';
import {map, takeUntil, tap}                                           from 'rxjs';
import {UiNumberFormatterService}                                      from '../../../logr-ui/utils/ui-number-formatter.service';
import {ChartDoughnutComponent}                                        from '../../comps/chart-doughnut/chart-doughnut.component';
import {ChartCatsBase}                                                 from '../chart-cats.base';

@Component({
			   selector   : 'app-chart-breakdown-day',
			   imports    : [
				   ChartDoughnutComponent
			   ],
			   templateUrl: './chart-breakdown-day.component.html',
			   styleUrl   : './chart-breakdown-day.component.css'
		   })
export class ChartBreakdownDayComponent extends ChartCatsBase<ChartDoughnutComponent, 'doughnut'> implements OnDestroy, AfterViewInit {
	@ViewChild('ChartWrapper') chartComponent!: ChartDoughnutComponent;
	svcUiNumberFormatter = inject(UiNumberFormatterService)


	conf: ChartConfiguration<'doughnut', number[]> = {
		data   : {
			datasets: this.svcChartDefaults.rizeDatasetsDoughnut(),
			labels  : [
				'Workouts',
				'Daylight',
				'Break',
				'Focus',
				'Meeting',
				'Other'
			]
		},
		options: this.svcChartProps.optsDoughnut,
		plugins: [],
		type   : 'doughnut'
	};

	date = input<Date>(new Date());

	constructor() {
		super();

		const tooltipConfig = {
			...this.conf.options?.plugins?.tooltip
		};

		if (this.conf.options?.plugins?.tooltip) {
			this.conf.options.plugins.tooltip = {
				...tooltipConfig,
				intersect: true,
				mode     : 'nearest',
				callbacks: {
					label: (tooltipItem: TooltipItem<'doughnut'>) => `${tooltipItem.label}: ${this.svcUiNumberFormatter.minsToClock(tooltipItem.parsed)}`.toUpperCase(),
					title: () => ''
				}
			}
		}
	}


	public ngAfterViewInit() {


		this.svcDataFactory.svcDataOura.recordsOuraSleep
			.pipe(takeUntil(this.dataTrash))
			.pipe(
				map(records => records
					.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromJSDate(record.bedtime_end), 'day'))
				),
				tap(records => console.log(records))
			)
			.subscribe(sleep => {
				if (!sleep.length) {
					throw ('No sleep record found')
					return
				}

				const bedtimeStart = DateTime.fromJSDate(sleep[0].bedtime_end);
				const now = DateTime.now();
				const totalTime = bedtimeStart ? now.diff(bedtimeStart, 'minutes').minutes : 0;

				let rizeFocus = 0
				let rizeBreak = 0
				let rizeMeets = 0

				let hkitWorks = 0;
				let hkitDaylight = 0;

				const timeSpent = () => rizeFocus + rizeBreak + rizeMeets + hkitWorks + hkitDaylight;

				this.svcDataFactory.svcDataHkit.recordsWorkouts
					.pipe(takeUntil(this.dataTrash))
					.pipe(map(records => records.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromJSDate(record.start.toDate()), 'day'))))
					.subscribe(records => {
						hkitWorks = records
							.map(r => DateTime.fromMillis(r.end.toMillis()).diff(DateTime.fromMillis(r.start.toMillis()), 'minutes').minutes)
							.reduce((a, b) => a + b, 0);
						this.chartComponent.updateSingleDataPoint(0, 0, hkitWorks)
						this.chartComponent.updateSingleDataPoint(0, 5, Math.max(0, totalTime - (timeSpent())))
					})

				this.svcDataFactory.svcDataHkit.getRecordsSum()
					.pipe(takeUntil(this.dataTrash))
					.pipe(map(records => records
						.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromJSDate(record.timestamp.toDate()), 'day'))
						.filter(record => record.type === 'time_in_daylight')
					))
					.subscribe(records => {

						hkitDaylight = records
							.map(r => r.qty)
							.reduce((a, b) => a + b, 0);
						this.chartComponent.updateSingleDataPoint(0, 1, hkitDaylight)
						this.chartComponent.updateSingleDataPoint(0, 5, Math.max(0, totalTime - (timeSpent())))
					});


				this.svcDataFactory.svcDataRize.rizeSessions
					.pipe(takeUntil(this.dataTrash))
					.pipe(map(records => records.filter(record => DateTime.fromJSDate(this.date()).hasSame(DateTime.fromJSDate(record.startTime), 'day'))))
					.subscribe(records => {
						// Calculate durations for focus, break, and meeting
						const calcTimeDiff = (type: RizeSessionType): number => records
							.filter(r => r.type === type)
							.map(r => DateTime.fromJSDate(r.endTime).diff(DateTime.fromJSDate(r.startTime), 'minutes').minutes)
							.reduce((a, b) => a + b, 0);

						rizeFocus = calcTimeDiff('focus');
						rizeBreak = calcTimeDiff('break');
						rizeMeets = calcTimeDiff('meeting');


						// Update the dataset with calculated values
						this.chartComponent.updateSingleDataPoint(0, 2, rizeBreak)
						this.chartComponent.updateSingleDataPoint(0, 3, rizeFocus)
						this.chartComponent.updateSingleDataPoint(0, 4, rizeMeets)
						this.chartComponent.updateSingleDataPoint(0, 5, Math.max(0, totalTime - (timeSpent())))
					});


			});


	}
}
